import url from 'url'
import {get,pickBy, identity, isEmpty} from 'lodash'
import {config} from '../../config'
import messages from '../../assets/messages'
import {Viewer} from '../types'
import {getItem} from './localStorage'
import {getCookie} from './browser'
import {readApiDomain, resolveEndpoints, bounceToPwa} from '../../vn-access/runtime-api'
import {readBearerToken} from '../../vn-access/token'
import {recoverFromVnFailure} from '../../vn-access/recovery'

/** REST base URL for a host already resolved this request: VN runtime host, or bounce + baked host. */
const resolveRestBase = (domain: string | null): string => {
  if (!config.vnAccess) return config.restEndpointUrl
  if (!domain) {
    bounceToPwa()
    return config.restEndpointUrl
  }
  return resolveEndpoints(domain).restEndpointUrl
}

export const backendRequest = async (endpoint: string, params : any = {}, method: string = 'POST', options : RequestInit = {}) => {
  const headers : Record<string, string> = get(options, 'headers', {'Content-Type': 'application/json'}) as Record<string, string>
  // Snapshot the runtime host ONCE per request: the same value gates the bearer and picks the base
  // URL below, so a domain cleared by a concurrent 401 across the `await` can never leave the token
  // attached while the URL falls back to the baked build host (PT-5648).
  const domain = config.vnAccess ? readApiDomain() : null
  if (domain) {
    const token = await readBearerToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  const reqOptions : RequestInit = {
    method,
    headers,
    body: (params ? JSON.stringify(params) : undefined),
    mode: 'cors' as const,
    credentials: config.vnAccess ? 'omit' as const : 'include' as const,
    ...options
  }
  try {
    const res = await fetch(url.resolve(resolveRestBase(domain), endpoint), reqOptions)
    // VN: a 401 means the bearer is revoked → clear it and bounce to the PWA to re-auth. Other
    // statuses (5xx, other 4xx) are a reachable host's error and flow through to the caller.
    if (config.vnAccess && res.status === 401) await recoverFromVnFailure('clear-and-bounce')
    return res
  } catch (err) {
    // VN: a response-less transport failure (host unreachable / rotated away) → bounce so the PWA
    // re-discovers a live host. Rethrow so existing callers' `.catch` still runs.
    if (config.vnAccess) await recoverFromVnFailure('bounce')
    throw err
  }
}

export const backendGetRequest = async (endpoint, credentials?: boolean, params?: any) => {
  const cleanedParams = pickBy(params, v => v !== undefined)
  if (!isEmpty(cleanedParams)) endpoint = `${endpoint}?${new URLSearchParams(cleanedParams).toString()}`
  const headers : Record<string, string> = {}
  // Snapshot the runtime host ONCE (see backendRequest): gating the bearer and choosing the base URL
  // off the same value keeps the token from ever leaking to the baked build host (PT-5648).
  const domain = config.vnAccess ? readApiDomain() : null
  if (domain) {
    const token = await readBearerToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  const reqOptions : RequestInit = {
    method: 'GET',
    headers,
    mode: config.vnAccess ? 'cors' : (credentials ? 'cors' : undefined),
    credentials: config.vnAccess ? 'omit' : (credentials ? 'include' : undefined)
  }
  try {
    const res = await fetch(url.resolve(resolveRestBase(domain), endpoint), reqOptions)
    // VN: a 401 means the bearer is revoked → clear it and bounce to the PWA to re-auth. Other
    // statuses (5xx, other 4xx) are a reachable host's error and flow through to the caller.
    if (config.vnAccess && res.status === 401) await recoverFromVnFailure('clear-and-bounce')
    return res
  } catch (err) {
    // VN: a response-less transport failure (host unreachable / rotated away) → bounce so the PWA
    // re-discovers a live host. Rethrow so existing callers' `.catch` still runs.
    if (config.vnAccess) await recoverFromVnFailure('bounce')
    throw err
  }
}

export const loginRequest = (email, password, recaptcha) =>
  backendRequest('/auth/login-web', {email, password, recaptcha})
    .then((res) => {
      if (res.ok) {
        return res.json()
      } else {
        if (res.status === 429) {
          throw new Error('tooManyRequests')
        } else {
          return res.json().then((result) => {
            const error = get(result, 'error')
            if (!error)
              throw messages.somethingWrongError.i18nKey
            else throw get(messages, error) ? error : result.message
          })
        }
      }
    })


export const logoutRequest = () =>
  backendRequest('/auth/logout?redirect=false')
    .then((res) => {
      if (res.ok) {
        return true
      } else {
        return res.json().then(({message}) => {
          const e = new Error(message)
          // removed because the prop doesn't exist
          // e.isHandled = true
          throw e
        })
      }
    })

export const resetPasswordRequest = (email) =>
  backendRequest('/auth/password-reset/start', {email})
    .then((res) => {
      if (res.ok) {
        return true
      } else {
        return res.json().then(({message}) => {
          throw message
        })
      }
    })

export const resetPasswordFinish = (newPassword, token) =>
  backendRequest('/auth/password-reset/finish', {newPassword, token})
    .then((res) => {
      if (res.ok) {
        return true
      } else {
        return res.json().then(({message}) => {
          throw message
        })
      }
    })

/** Preview partner-legal acceptance token (no login). */
export const partnerLegalAcceptancePreview = (token) =>
  backendGetRequest(`/auth/partner-legal-acceptance?token=${encodeURIComponent(token)}`)
    .then((res) => {
      if (res.ok) {
        return res.json()
      }
      return res.json().then(({message}) => {
        throw message || 'invalidToken'
      })
    })

/** Confirm partner-legal acceptance via emailed token (no login). */
export const partnerLegalAcceptanceFinish = (token) =>
  backendRequest('/auth/partner-legal-acceptance/finish', {
    token,
    ageAcceptance: true,
    legalAcceptance: true,
  }).then((res) => {
    if (res.ok) {
      return true
    }
    return res.json().then(({message}) => {
      throw message || 'invalidToken'
    })
  })

export const getRelatedEntities = () =>
  backendGetRequest('/auth/client/related-entities', true)
    .then((res) =>res.json())
    .catch((error)=>error.json())

export const register = (firstName,lastName,currency,phoneCode,phoneValue,email,promoCode,password,country,ageAcceptance,
  riskAcceptance,eulaAcceptance,privacyAcceptance,products,accountSubtype,optInMarketing,locale,
  affiliateCountries,bta,am,company,whiteLabel,isCorporate, customDomain, birthday, checkField, recaptcha,
  referralCode, referral, regulationAcceptance, partnerReferenceNumber, licenseAcknowledgement, multiRegulationClientId, solicitationAcceptance, otpSecret = undefined ) =>{
  const phone = `${phoneCode}${phoneValue}`

  const nullpointIbIdFromCookie = Number(getCookie('nullpointIbId'))
  const nullpointIbId = Number.isInteger(nullpointIbIdFromCookie) ? nullpointIbIdFromCookie : undefined

  const nullpointIbCampaign = getCookie('nullpointIbCampaign')
  const partnerManagerId = getCookie('partnerManagerId')

  const params ={firstName,lastName,currency,phone,email,promoCode,password,country,ageAcceptance,riskAcceptance,
    eulaAcceptance,privacyAcceptance,products,accountSubtype,optInMarketing,locale,
    affiliateCountries,bta,am,company,whiteLabel,isCorporate,customDomain, birthday, checkField, recaptcha,
    referralCode, referral, regulationAcceptance, partnerReferenceNumber, licenseAcknowledgement, multiRegulationClientId, solicitationAcceptance, nullpointIbId, nullpointIbCampaign, partnerManagerId, otpSecret}
  return backendRequest('api/signup-web',pickBy(params,identity))
    .then((res) =>res.json())
    .catch((error)=>error.json())
}
export const checkEmail = (email: string,company?: string,product?: string) =>
  backendRequest('api/check-email',{email,company,product})
    .then((res) =>{
      if (res.ok) {
        return false
      } else {
        return res.json().then((result) => result.key)
      }
    }).catch((error)=>error.json())

export const getIPCountry = () =>
  backendGetRequest('/api/get-country', true)
    .then((res) => res.json())
    .catch((error)=>error)

export const getSignupCountries = (company) =>
  backendGetRequest(`/api/signup-countries?company=${company}`)
    .then((res) => res.json())
    .catch((error)=>error)

export const getAutochartistDetails = (locale) =>
  backendGetRequest(`/api/getAutochartistDetails?locale=${locale}`, true)
    .then((res) => res.json())
    .catch((error)=>error)

export const getStrategiesDetails = (accountId) =>
  backendGetRequest(`/api/v1/crm/strategies/${accountId}`, true)
    .then((res) => res.json())
    .catch((error)=> error)

export const getLeaderboardDetails = (competitionId) =>
  backendGetRequest(`/api/v1/marketing/leaderboards/${competitionId}`, true)
    .then((res) => res.json())
    .catch((error)=> error)

export const getLeaderboardClientDetails = (competitionId, clientId) =>
  backendGetRequest(`/api/v1/marketing/leaderboards/${competitionId}/members/${clientId}`, true)
    .then((res) => res.json())
    .catch((error)=> error)

export const getMemberFromLeaderboard = (competitionId, clientId) =>
  backendGetRequest(`/api/v1/marketing/leaderboards/${competitionId}/${clientId}`, true)
    .then((res) => res.json())
    .catch((error)=> error)

export const checkReferralCode = (referralCode, referral) =>
  backendRequest('api/check-referral-code',{referralCode, referral})
    .then((res) => res.json()).catch((error) => error.json())

export const startCopying = (accountId, providerAccountId) =>
  backendRequest(`/api/v1/crm/strategies/${accountId}/start_copying/${providerAccountId}`)
    .then((res) => res.json())
    .catch((error)=> error)

export const stopCopying = (accountId) =>
  backendRequest(`/api/v1/crm/strategies/${accountId}/stop_copying`)
    .then((res) => res.json())
    .catch((error)=> error)

export const getConfigVars = () =>
  backendGetRequest('app-config')
    .then((res) => res.json()).catch((error) => error.json())

export const loginVerificationRequest = (token) =>
  backendRequest('/auth/login-verification', {token})
    .then((res) => {
      if (res.ok) {
        return true
      } else {
        return res.json().then(({error}) => {
          throw error
        })
      }
    })

export const getStrategiesSummary = (accountId, month, year) =>
  backendGetRequest(`/api/v1/crm/strategies/${accountId}/summary`, true, {month, year})
    .then((res) => {
      if (res.ok) {
        return res.json()
      } else {
        return false
      }
    })
    .catch((error)=> error)

export const getStrategiesFollowers = (accountId, month, year, page) =>
  backendGetRequest(`/api/v1/crm/strategies/${accountId}/followers`, true, {month, year, page})
    .then((res) => {
      if (res.ok) {
        return res.json()
      } else {
        return false
      }
    })
    .catch((error)=> error)

export const getCampaignCookie = () =>
  backendGetRequest('/api/get-campaign-cookie', true)
    .then((res) =>res.json())
    .catch((error)=>error)

export const getCountries = (product?, company?) =>
  backendGetRequest('/api/get-countries-web', true, {product,company})
    .then((res) => res.json().then((res) => res)
      .catch((error)=>error)
    ).catch((error)=>error)

export const getTradeCompanionDetails = () =>
  backendGetRequest('/api/get-trade-companion-details', true)
    .then((res) => res.json()).catch((error)=>error)

export const eSignDocument = (paymentProvider, amount) =>
  backendRequest('/api/external/eSignGenie', {paymentProvider, amount})
    .then((res) => res.json())
    .catch((error)=>error)

export const eSignDocumentConfirmation = (paymentProvider, folderId, folderName) =>
  backendRequest('/api/external/eSignGenie/confirmSigning', {paymentProvider, folderId, folderName})
    .then((res) => res.json())
    .catch((error)=>error)

export const setCampaignCookie = (queryParams) =>
  backendRequest('/api/set-campaign-cookie', {queryParams})
    .then((res) => res.json())
    .catch((error) => error)

export const authSession = () =>
  backendRequest('/auth/session')
    .then((res) => res.json())
    .catch((error) => error)

export const getExternalAccountData = (params) => {
  const reqOptions : RequestInit = {
    // 'Content-Type': 'application/json',
    method: 'POST' as const,
    body: (params ? JSON.stringify(params) : undefined),
  }

  const url = config.externalAccountURL + '?' + (new URLSearchParams(params)).toString()

  return fetch(url, reqOptions).then((res) => {
    if (res.ok) {
      return res.json()
    } else {
      return undefined
    }
  })
    .catch((error)=> error)
}

export const getLocale = (viewer: Viewer) : string => {
  const locale : any = get(viewer, 'locale') || getItem('locale', 'en')
  return locale
}
